/*
 * Observability dashboard — surfaces the basics every operator wants:
 * adapter health, job queue depth, error rate, latency p50/p95, and
 * a per-job-type breakdown over the last 24h.
 *
 * Backed by /api/observability/summary which derives everything from
 * existing data (async-jobs + forwarding-runs + adapter probes) — no
 * new metrics infrastructure required for this MVP. Phase 7+ can layer
 * real histograms on top.
 */

import { useEffect, useState } from "react";
import PageBreadcrumbs from "../layouts/PageBreadcrumbs.js";
import { api, ApiError } from "../lib/api.js";
import { SkeletonStat, SkeletonTable } from "../components/Skeleton.js";

type Summary = Awaited<ReturnType<typeof api.observabilitySummary>>;

function fmtMs(ms: number): string {
  if (!ms) return "—";
  if (ms < 1000) return `${Math.round(ms)} ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)} s`;
  return `${Math.round(s / 60)} m ${Math.round(s % 60)} s`;
}

function fmtPct(rate: number): string {
  return `${(rate * 100).toFixed(1)}%`;
}

export default function Observability() {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = () => {
    api.observabilitySummary()
      .then((s) => setSummary(s))
      .catch((e) => setError(e instanceof ApiError ? e.message : String(e)));
  };
  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 30_000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>Observability</h1>
          <p className="muted">Adapter health, queue depth, error rate, and latency over the last 24 hours.</p>
        </div>
        <div className="actions">
          <button type="button" className="btn btn-secondary" onClick={refresh}>Refresh</button>
        </div>
      </header>

      {error && <div className="ts-alert ts-alert-error">{error}</div>}
      {!summary && (
        <>
          <section className="stat-grid">
            <SkeletonStat /><SkeletonStat /><SkeletonStat /><SkeletonStat />
          </section>
          <section className="section">
            <div className="section-head"><h2>Adapters</h2></div>
            <SkeletonTable rows={3} columns={2} />
          </section>
        </>
      )}

      {summary && (
        <>
          <section className="stat-grid">
            <div className="stat">
              <div className="stat-label">Job error rate (24h)</div>
              <div className="stat-value" style={{ color: summary.jobs.errorRate > 0.05 ? "#b91c1c" : undefined }}>
                {fmtPct(summary.jobs.errorRate)}
              </div>
              <div className="stat-hint">{summary.jobs.failed} failed of {summary.jobs.total}</div>
            </div>
            <div className="stat">
              <div className="stat-label">Queue depth</div>
              <div className="stat-value">{summary.jobs.queued}</div>
              <div className="stat-hint">{summary.jobs.running} running</div>
            </div>
            <div className="stat">
              <div className="stat-label">Job latency p50</div>
              <div className="stat-value">{fmtMs(summary.jobs.latencyMs.p50)}</div>
              <div className="stat-hint">p95 {fmtMs(summary.jobs.latencyMs.p95)} · p99 {fmtMs(summary.jobs.latencyMs.p99)}</div>
            </div>
            <div className="stat">
              <div className="stat-label">Forwarding failure rate</div>
              <div className="stat-value" style={{ color: summary.forwarding.failureRate > 0.05 ? "#b91c1c" : undefined }}>
                {fmtPct(summary.forwarding.failureRate)}
              </div>
              <div className="stat-hint">{summary.forwarding.failedRuns} failed of {summary.forwarding.totalRuns}</div>
            </div>
          </section>

          <section className="section">
            <div className="section-head">
              <h2>Adapters</h2>
              <span className="muted" style={{ fontSize: 12 }}>checked {new Date(summary.checkedAt).toLocaleTimeString()}</span>
            </div>
            <table className="transfer-table">
              <thead>
                <tr><th>Adapter</th><th>Status</th></tr>
              </thead>
              <tbody>
                {Object.entries(summary.adapters).map(([name, ok]) => (
                  <tr key={name}>
                    <td className="mono" style={{ fontSize: 12 }}>{name}</td>
                    <td>{ok ? <span className="pill pill-ok">healthy</span> : <span className="pill pill-err">down</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <section className="section">
            <div className="section-head"><h2>Jobs by type (last 24h)</h2></div>
            {Object.keys(summary.jobs.byType).length === 0 ? (
              <div className="empty-state-card">
                <h2 style={{ marginTop: 0 }}>No jobs in the last 24 hours</h2>
                <p>Trigger a sync from any console — every job lands here with success / failure breakdown.</p>
              </div>
            ) : (
              <table className="transfer-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th style={{ textAlign: "right" }}>Total</th>
                    <th style={{ textAlign: "right" }}>Completed</th>
                    <th style={{ textAlign: "right" }}>Failed</th>
                    <th style={{ textAlign: "right" }}>Failure rate</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(summary.jobs.byType).map(([type, stats]) => {
                    const rate = stats.total > 0 ? stats.failed / stats.total : 0;
                    return (
                      <tr key={type}>
                        <td className="mono" style={{ fontSize: 12 }}>{type}</td>
                        <td style={{ textAlign: "right" }}>{stats.total}</td>
                        <td style={{ textAlign: "right" }}>{stats.completed}</td>
                        <td style={{ textAlign: "right" }}>{stats.failed}</td>
                        <td style={{ textAlign: "right" }}>
                          <span className={rate > 0.05 ? "pill pill-err" : "pill pill-muted"}>{fmtPct(rate)}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </section>
        </>
      )}
    </div>
  );
}
